import React, { useEffect, useId, useRef, useState } from 'react';
import Section from '../Section';
import HelpArticleLink from './HelpArticleLink';
import './SellerActionCenter.css';

/**
 * @typedef {Object} SellerAction
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} href
 * @property {string} icon
 * @property {string} helpQuery
 * @property {string} [cta]
 */

/**
 * @typedef {Object} SellerActionCategory
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {string} icon
 * @property {SellerAction[]} actions
 */

export const SELLER_ACTION_CATEGORIES = [
    {
        id: 'products',
        title: 'Manage Products',
        description: 'Create, organize, import, and promote your catalog.',
        icon: 'fa-solid fa-boxes-stacked',
        actions: [
            {
                id: 'create-product',
                title: 'Create a Product',
                description: 'Launch the product creator and build a new listing.',
                href: '/account/products#create',
                icon: 'fa-solid fa-plus',
                helpQuery: 'create a product',
            },
            {
                id: 'product-catalog',
                title: 'Product Catalog',
                description: 'Edit, sort, publish, or update your existing products.',
                href: '/account/products',
                icon: 'fa-solid fa-box-open',
                helpQuery: 'manage products',
            },
            {
                id: 'folders',
                title: 'Folders',
                description: 'Group related products into storefront collections.',
                href: '/account/products/folders',
                icon: 'fa-solid fa-folder-tree',
                helpQuery: 'product folders',
            },
            {
                id: 'import-products',
                title: 'Import Products',
                description: 'Bring in listings from Etsy, Shopify, or Printify.',
                href: '/account/products#import',
                icon: 'fa-solid fa-file-import',
                helpQuery: 'import products',
            },
            {
                id: 'coupons',
                title: 'Coupons',
                description: 'Create and manage discounts for your products.',
                href: '/account/products/coupons',
                icon: 'fa-solid fa-tags',
                helpQuery: 'product coupons discounts',
            },
            {
                id: 'export-products',
                title: 'Export Products',
                description: 'Download your catalog as a CSV spreadsheet.',
                href: '/account/products#export',
                icon: 'fa-solid fa-file-export',
                helpQuery: 'export products csv',
            },
        ],
    },
    {
        id: 'invoices',
        title: 'Invoices',
        description: 'Bill customers and keep track of balances.',
        icon: 'fa-solid fa-file-invoice-dollar',
        actions: [
            {
                id: 'manage-invoices',
                title: 'Create & Manage Invoices',
                description: 'Send an invoice, record payments, and follow outstanding balances.',
                href: '/account/invoices',
                icon: 'fa-solid fa-file-circle-plus',
                helpQuery: 'create manage invoices',
            },
            {
                id: 'invoice-customers',
                title: 'Customer Directory',
                description: 'Find customer details and review their purchase history.',
                href: '/account/subscribers',
                icon: 'fa-solid fa-address-book',
                helpQuery: 'customers subscribers',
            },
            {
                id: 'payment-settings',
                title: 'Invoice Payment Settings',
                description: 'Choose how customers can pay you.',
                href: '/account/payments/links',
                icon: 'fa-solid fa-credit-card',
                helpQuery: 'accept payments payment methods',
            },
        ],
    },
    {
        id: 'orders',
        title: 'Orders',
        description: 'Fulfill sales, print labels, and handle taxes.',
        icon: 'fa-solid fa-cart-shopping',
        actions: [
            {
                id: 'manage-orders',
                title: 'Manage Orders',
                description: 'Review purchases, payment status, and fulfillment details.',
                href: '/account/orders',
                icon: 'fa-solid fa-clipboard-list',
                helpQuery: 'manage orders',
            },
            {
                id: 'bookings',
                title: 'Bookings & Scheduling',
                description: 'Manage calendars, availability, and customer reservations.',
                href: '/account/bookings',
                icon: 'fa-solid fa-calendar-check',
                helpQuery: 'bookings scheduling availability',
            },
            {
                id: 'shipping-labels',
                title: 'Shipping Labels',
                description: 'Print packing slips, address labels, and return labels.',
                href: '/account/orders/labels',
                icon: 'fa-solid fa-truck-fast',
                helpQuery: 'shipping labels fulfillment',
            },
            {
                id: 'tax-center',
                title: 'Tax Center',
                description: 'Review taxable sales and download tax reports.',
                href: '/account/orders/tax-center',
                icon: 'fa-solid fa-building-columns',
                helpQuery: 'seller tax center',
            },
            {
                id: 'customer-reviews',
                title: 'Customer Reviews',
                description: 'Read and respond to reviews from your customers.',
                href: '/account/reviews',
                icon: 'fa-solid fa-star',
                helpQuery: 'customer reviews',
            },
        ],
    },
    {
        id: 'website',
        title: 'Website',
        description: 'Shape your storefront, content, and branding.',
        icon: 'fa-solid fa-store',
        actions: [
            {
                id: 'storefront-basics',
                title: 'Storefront Settings',
                description: 'Update your storefront address, basics, imagery, and location.',
                href: '/account/landingpage',
                icon: 'fa-solid fa-sliders',
                helpQuery: 'storefront setup settings',
            },
            {
                id: 'branding',
                title: 'Branding Settings',
                description: 'Choose your colors, fonts, layout, and visual style.',
                href: '/account/landingpage?section=branding',
                icon: 'fa-solid fa-palette',
                helpQuery: 'storefront branding colors',
            },
            {
                id: 'translations',
                title: 'Translation Settings',
                description: 'Set storefront languages, currency, and international options.',
                href: '/account/landingpage?section=internationalization',
                icon: 'fa-solid fa-language',
                helpQuery: 'storefront translations languages',
            },
            {
                id: 'custom-pages',
                title: 'Custom Pages',
                description: 'Create rich pages for information, portfolios, or policies.',
                href: '/account/pages',
                icon: 'fa-solid fa-file-lines',
                helpQuery: 'custom storefront pages',
            },
            {
                id: 'links-socials',
                title: 'Links & Friends',
                description: 'Feature important links and recommend other Mayhem creators.',
                href: '/account/landingpage/links',
                icon: 'fa-solid fa-link',
                helpQuery: 'storefront links friends',
            },
            {
                id: 'social-profiles',
                title: 'Social Profiles',
                description: 'Connect your other social accounts to your storefront.',
                href: '/account/landingpage/socials',
                icon: 'fa-solid fa-share-nodes',
                helpQuery: 'storefront social profiles',
            },
            {
                id: 'blog',
                title: 'Blog',
                description: 'Publish updates and keep your audience engaged.',
                href: '/account/blog',
                icon: 'fa-solid fa-pen-nib',
                helpQuery: 'write blog post',
            },
            {
                id: 'newsletters',
                title: 'Email Newsletters',
                description: 'Write and send email to subscribers who opted in.',
                href: '/account/newsletters',
                icon: 'fa-solid fa-paper-plane',
                helpQuery: 'email newsletters',
            },
        ],
    },
    {
        id: 'payments',
        title: 'Payments & Payouts',
        description: 'Configure payments and manage your earnings.',
        icon: 'fa-solid fa-money-check-dollar',
        actions: [
            {
                id: 'accept-payments',
                title: 'Accept Payments',
                description: 'Connect and configure the ways your customers can pay.',
                href: '/account/payments/links',
                icon: 'fa-solid fa-credit-card',
                helpQuery: 'accept payments payment methods',
            },
            {
                id: 'payout-profile',
                title: 'Payout Profile',
                description: 'Manage the identity, tax, and bank details used for payouts.',
                href: '/account/payments/profile',
                icon: 'fa-solid fa-id-card',
                helpQuery: 'payout profile',
            },
            {
                id: 'verification',
                title: 'Verification Center',
                description: 'Verify your identity and manage your Verified Seller status.',
                href: '/account/verification',
                icon: 'fa-solid fa-circle-check',
                helpQuery: 'verified seller verification',
            },
            {
                id: 'payouts',
                title: 'Payouts',
                description: 'Review payout history or request an available payout.',
                href: '/account/payments/payouts',
                icon: 'fa-solid fa-money-bill-transfer',
                helpQuery: 'seller payouts',
            },
            {
                id: 'affiliate-codes',
                title: 'Affiliate Codes',
                description: 'Create referral codes, track commissions, and review earnings.',
                href: '/account/affiliates',
                icon: 'fa-solid fa-people-arrows',
                helpQuery: 'affiliate codes seller referrals',
            },
            {
                id: 'tax-information',
                title: 'Tax Information',
                description: 'Complete or update the tax details for your payout account.',
                href: '/account/payments/payouts/onboarding/tax',
                icon: 'fa-solid fa-file-signature',
                helpQuery: 'payout tax information',
            },
            {
                id: 'billing-history',
                title: 'Billing History',
                description: 'Review charges related to your Mayhem plan.',
                href: '/account/payments/billing-history',
                icon: 'fa-solid fa-receipt',
                helpQuery: 'billing history subscription',
            },
        ],
    },
    {
        id: 'integrations',
        title: 'Integrations & Tools',
        description: 'Connect services and use seller utilities.',
        icon: 'fa-solid fa-puzzle-piece',
        actions: [
            {
                id: 'integrations',
                title: 'Manage Integrations',
                description: 'Connect Printify and review available seller integrations.',
                href: '/account/integrations',
                icon: 'fa-solid fa-plug',
                helpQuery: 'seller integrations',
            },
            {
                id: 'qr-code',
                title: 'QR Code Generator',
                description: 'Make a scannable code for your storefront, products, or any URL.',
                href: '/account/integrations#qr-code-generator',
                icon: 'fa-solid fa-qrcode',
                helpQuery: 'qr code generator',
            },
            {
                id: 'media-library',
                title: 'Media Library',
                description: 'Upload and manage reusable product and storefront media.',
                href: '/account/media',
                icon: 'fa-solid fa-photo-film',
                helpQuery: 'media library uploads',
            },
            {
                id: 'custom-domain',
                title: 'Custom Domain',
                description: 'Connect your own domain name to your Mayhem storefront.',
                href: '/account/integrations/custom-domain',
                icon: 'fa-solid fa-globe',
                helpQuery: 'custom storefront domain',
            },
            {
                id: 'account-plan',
                title: 'Account & Plan',
                description: 'Manage your seller plan, upgrades, and account preferences.',
                href: '/account/plan',
                icon: 'fa-solid fa-user-gear',
                helpQuery: 'seller plan account settings',
            },
        ],
    },
    {
        id: 'analytics',
        title: 'Analytics',
        description: 'Understand traffic, products, and customers.',
        icon: 'fa-solid fa-chart-line',
        actions: [
            {
                id: 'storefront-analytics',
                title: 'Storefront Analytics',
                description: 'Explore pageviews, visits, and traffic trends.',
                href: '/account/analytics',
                icon: 'fa-solid fa-chart-area',
                helpQuery: 'storefront analytics pageviews',
            },
            {
                id: 'sales-performance',
                title: 'Sales Performance',
                description: 'Use your order history to review sales and fulfillment activity.',
                href: '/account/orders',
                icon: 'fa-solid fa-arrow-trend-up',
                helpQuery: 'sales order analytics',
            },
            {
                id: 'customer-insights',
                title: 'Customer Insights',
                description: 'Review your customers and their purchase history.',
                href: '/account/subscribers',
                icon: 'fa-solid fa-users-viewfinder',
                helpQuery: 'customer analytics subscribers',
            },
        ],
    },
    {
        id: 'help',
        title: 'Learn and Get Help',
        description: 'Find answers, contact support, and connect with other sellers.',
        icon: 'fa-solid fa-graduation-cap',
        actions: [
            {
                id: 'support-tickets',
                title: 'Support Tickets',
                description: 'Review your current and previous conversations with support.',
                href: '/account/help#support-tickets',
                icon: 'fa-solid fa-ticket',
                helpQuery: 'seller support tickets',
                cta: 'View tickets',
            },
            {
                id: 'new-support-request',
                title: 'Open a Support Request',
                description: 'Send the Mayhem team a question or report a problem.',
                href: '/account/help#new-support-request',
                icon: 'fa-solid fa-life-ring',
                helpQuery: 'contact seller support',
                cta: 'Get support',
            },
            {
                id: 'seller-help-articles',
                title: 'Seller Help Articles',
                description: 'Browse guidance selected specifically for Mayhem sellers.',
                href: '/account/help#help-articles',
                icon: 'fa-solid fa-book-open',
                helpQuery: 'getting started seller',
                cta: 'Browse articles',
            },
            {
                id: 'help-center',
                title: 'Search the Help Center',
                description: 'Search all available help articles and how-to guides.',
                href: '/help',
                icon: 'fa-solid fa-magnifying-glass',
                helpQuery: 'help center',
                cta: 'Search help',
            },
            {
                id: 'seller-forums',
                title: 'Seller Forums',
                description: 'Learn from other sellers, share ideas, and join discussions.',
                href: '/account/help/forums',
                icon: 'fa-solid fa-comments',
                helpQuery: 'seller forums community',
                cta: 'Visit forums',
            },
            {
                id: 'product-updates',
                title: 'Updates & Roadmap',
                description: 'See what is shipping now and what the Mayhem team is building.',
                href: '/roadmap',
                icon: 'fa-solid fa-map',
                helpQuery: 'Mayhem updates roadmap',
                cta: 'View updates',
            },
        ],
    },
];

export function SellerActionTile({ action }) {
    return (
        <article className="seller-action-tile">
            <a
                className="seller-action-tile__main"
                href={action.href}
                aria-label={`Open ${action.title}`}
            >
                <span className="seller-action-tile__icon" aria-hidden="true">
                    <i className={action.icon}></i>
                </span>
                <span className="seller-action-tile__copy">
                    <strong>{action.title}</strong>
                    <span>{action.description}</span>
                </span>
                <span className="seller-action-tile__open">
                    {action.cta || 'Open tool'}
                    <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </span>
            </a>
            <div className="seller-action-tile__help">
                <HelpArticleLink
                    query={action.helpQuery}
                    label={`${action.title} help`}
                    variant="link"
                />
            </div>
        </article>
    );
}

export function SellerActionCategoryTile({ category, isExpanded, panelId, onToggle }) {
    return (
        <button
            type="button"
            className={[
                'seller-action-category',
                `seller-action-category--${category.id}`,
                isExpanded ? 'seller-action-category--expanded' : '',
            ].filter(Boolean).join(' ')}
            aria-expanded={isExpanded}
            aria-controls={panelId}
            onClick={onToggle}
        >
            <span className="seller-action-category__icon" aria-hidden="true">
                <i className={category.icon}></i>
            </span>
            <span className="seller-action-category__copy">
                <strong>{category.title}</strong>
                <span>{category.description}</span>
            </span>
            <span className="seller-action-category__meta">
                {category.actions.length} {category.actions.length === 1 ? 'tool' : 'tools'}
                <i className="fa-solid fa-chevron-down" aria-hidden="true"></i>
            </span>
        </button>
    );
}

export default function SellerActionCenter({
    categories = SELLER_ACTION_CATEGORIES,
    defaultCategory = null,
}) {
    const instanceId = useId().replace(/:/g, '');
    const validDefault = categories.some(category => category.id === defaultCategory)
        ? defaultCategory
        : null;
    const [expandedCategoryId, setExpandedCategoryId] = useState(validDefault);
    const expandedCategory = categories.find(category => category.id === expandedCategoryId);
    const panelId = `seller-action-panel-${instanceId}`;
    const panelRef = useRef(null);
    const shouldScrollToPanel = useRef(false);

    useEffect(() => {
        if (!expandedCategoryId || !shouldScrollToPanel.current) return;
        shouldScrollToPanel.current = false;

        if (
            typeof window.matchMedia !== 'function'
            || !window.matchMedia('(max-width: 560px)').matches
        ) {
            return;
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        panelRef.current?.scrollIntoView?.({
            behavior: reduceMotion ? 'auto' : 'smooth',
            block: 'start',
        });
    }, [expandedCategoryId]);

    function toggleCategory(categoryId) {
        shouldScrollToPanel.current = expandedCategoryId !== categoryId;
        setExpandedCategoryId(current => current === categoryId ? null : categoryId);
    }

    return (
        <Section
            id="seller-action-center"
            className="seller-action-center"
            title="What do you want to do?"
            preview="Choose an area to see shortcuts for common seller tasks."
        >
            <header className="seller-action-center__header">
                <p>
                    <strong>Seller shortcuts</strong>
                    Choose an area, then jump straight to the tool you need.
                </p>
                <span className="seller-action-center__spark" aria-hidden="true">
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                </span>
            </header>

            {!expandedCategory && (
                <div className="seller-action-center__categories">
                    {categories.map(category => (
                        <SellerActionCategoryTile
                            key={category.id}
                            category={category}
                            isExpanded={category.id === expandedCategoryId}
                            panelId={panelId}
                            onToggle={() => toggleCategory(category.id)}
                        />
                    ))}
                </div>
            )}

            {expandedCategory && (
                <div
                    ref={panelRef}
                    id={panelId}
                    className={`seller-action-panel seller-action-panel--${expandedCategory.id}`}
                    role="region"
                    aria-label={`${expandedCategory.title} tools`}
                >
                    <button
                        type="button"
                        className="seller-action-panel__back"
                        onClick={() => toggleCategory(expandedCategory.id)}
                    >
                        <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
                        Back to Categories
                    </button>
                    <div className="seller-action-panel__heading">
                        <span className="seller-action-panel__icon" aria-hidden="true">
                            <i className={expandedCategory.icon}></i>
                        </span>
                        <div>
                            <p>Explore {expandedCategory.title}</p>
                            <h3>Choose your next task</h3>
                        </div>
                    </div>
                    <div className="seller-action-panel__grid">
                        {expandedCategory.actions.map(action => (
                            <SellerActionTile key={action.id} action={action} />
                        ))}
                    </div>
                </div>
            )}
        </Section>
    );
}
